import { useFormContext } from "react-hook-form";
import CustomNotifications from "./CustomNotifications";
import { SmartForm } from "@avenueops/web-components";
import { trpc, trpc2 } from "utils/trpc";
import { useMemo } from "react";
import { getIntegrations } from "components/notifications/utils";
import validateNotificationInput from "utils/validateNotificationInput";
import { NotificationType } from "@prisma/client";
import { NotificationTypeMapToFilters } from "@avenueops/frontend-shared";
import Skeleton from "react-loading-skeleton";
import { DescribeMonitorRadioGroupValues } from "@avenueops/shared-types";

export function SetIntegrationWebhookTrigger({ type }: { type: DescribeMonitorRadioGroupValues }): JSX.Element | null {
  const { watch, setError } = useFormContext();

  const selectedIntegrationId = watch("integrationWebhookTrigger.organizationIntegrationId");
  const linkSubscriber = watch("integrationWebhookTrigger.meta.linkSubscriber");

  const { data: integrations } = trpc.integrations.list.useQuery();
  const filteredIntegrations = useMemo(() => getIntegrations({ integrations, selectedIntegrationId }), [integrations, selectedIntegrationId]);
  const { slackIntegrations } = filteredIntegrations;
  const { data: slackRecipientOptions, isLoading: recipientsLoading } = trpc2.v2.slack.getRecipients.useQuery(
    { integrationId: selectedIntegrationId },
    { enabled: !!selectedIntegrationId },
  );

  return (
    <div className="flex flex-col gap-10">
      <div className="w-full text-sm">
        <div className="font-medium text-gray-900">Which Slack channels should this trigger apply to?</div>
        <div className="text-gray-600">
          {type === DescribeMonitorRadioGroupValues.REACTION_ADDED ? (
            <div>Trigger a workflow when a "ticket" 🎫 emoji is added to a thread in any of the channels selected below.</div>
          ) : type === DescribeMonitorRadioGroupValues.NEW_MESSAGE ? (
            <div>Trigger a workflow when a new message is sent in any of the channels selected below. Please note, replies will not trigger the workflow.</div>
          ) : null}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <SmartForm.DropdownSelect
          defaultValue={slackIntegrations && slackIntegrations.length === 1 ? slackIntegrations[0].integrationId : undefined}
          name={"integrationWebhookTrigger.organizationIntegrationId"}
          title="Slack Workspace"
          options={
            slackIntegrations?.map((integration) => ({
              label: integration.name ?? "Slack Workspace",
              value: integration.integrationId,
            })) ?? []
          }
        />
        {selectedIntegrationId ? (
          !recipientsLoading ? (
            <SmartForm.DropdownMultiSelect
              name={"integrationWebhookTrigger.meta.channels"}
              title="Slack Channel or Member ID"
              options={slackRecipientOptions ?? []}
              placeholder="#general, @U123ABC (Slack member ID)"
              onCreate={(searchValue) => {
                if (searchValue) {
                }
                const valid = validateNotificationInput(searchValue, NotificationType.SLACK);
                if (!valid) {
                  setError("integrationWebhookTrigger.meta.channels", { type: "custom", message: "Invalid Slack channel or username format" });
                  return;
                }
                return { label: searchValue, value: searchValue };
              }}
            />
          ) : (
            <Skeleton />
          )
        ) : (
          <div className="text-sm text-gray-700">Select a workspace to see the available channels.</div>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <SmartForm.Checkbox label="Link Slack thread as an Avenue subscriber" name="integrationWebhookTrigger.meta.linkSubscriber" />
        {linkSubscriber ? (
          <CustomNotifications
            notificationPrefix={"integrationWebhookTrigger.meta"}
            notificationType={NotificationType.SLACK}
            supportedTypes={NotificationTypeMapToFilters[NotificationType.SLACK]}
          />
        ) : null}
      </div>
    </div>
  );
}
